import { useEffect, useMemo, useState } from 'react';
import axios, { AxiosInstance } from 'axios';
import { Card, Form, InputGroup, Button, ListGroup, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlusCircle,
  faPen,
  faTrash,
  faSave,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

type Todo = {
  id: string | number;
  text: string;
  done: boolean;
  created_at?: string;
};

type Props = {
  ticketId: string | number;
  token: string;
  apiBase?: string; // default: https://api20.easychatdesk.com
  className?: string;
};

/**
 * Backend endpoints (adjust if yours differ):
 * GET    /tickets/:ticketId/todos                 -> Todo[] { id, text, done }
 * POST   /tickets/:ticketId/todos                 -> body: { text }           -> Todo
 * PATCH  /tickets/:ticketId/todos/:todoId         -> body: { text?, done? }   -> Todo
 * DELETE /tickets/:ticketId/todos/:todoId
 */
export default function TicketTodoCard({ ticketId, token, apiBase = 'https://api20.easychatdesk.com', className }: Props) {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [busyId, setBusyId] = useState<string | number | null>(null);
  const [newText, setNewText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // edit state
  const [editId, setEditId] = useState<string | number | null>(null);
  const [editText, setEditText] = useState('');

  const api: AxiosInstance = useMemo(() => {
    const inst = axios.create({
      baseURL: apiBase,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      withCredentials: false
    });
    return inst;
  }, [apiBase, token]);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/tickets/${ticketId}/todos`);
      setTodos(Array.isArray(data) ? data : data?.todos || []);
    } catch (e: any) {
      console.error(e);
      setError('Failed to load to-dos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ticketId) return;
    fetchTodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const addTodo = async () => {
    const text = newText.trim();
    if (!text) return;
    try {
      setError(null);
      setBusyId('new');
      // optimistic
      const tempId = `tmp_${Date.now()}`;
      const optimistic: Todo = { id: tempId, text, done: false };
      setTodos((prev) => [optimistic, ...prev]);
      setNewText('');

      const { data } = await api.post(`/tickets/${ticketId}/todos`, { text });
      // replace temp with real
      setTodos((prev) =>
        prev.map((t) => (t.id === tempId ? (data?.todo || data) : t))
      );
    } catch (e: any) {
      console.error(e);
      setError('Failed to add to-do.');
      // revert optimistic
      setTodos((prev) => prev.filter((t) => !String(t.id).startsWith('tmp_')));
    } finally {
      setBusyId(null);
    }
  };

  const toggleDone = async (todo: Todo) => {
    try {
      setBusyId(todo.id);
      // optimistic
      setTodos((prev) =>
        prev.map((t) => (t.id === todo.id ? { ...t, done: !t.done } : t))
      );
      await api.patch(`/tickets/${ticketId}/todos/${todo.id}`, { done: !todo.done });
    } catch (e: any) {
      console.error(e);
      setError('Failed to update to-do.');
      // revert
      setTodos((prev) =>
        prev.map((t) => (t.id === todo.id ? { ...t, done: todo.done } : t))
      );
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (todo: Todo) => {
    setEditId(todo.id);
    setEditText(todo.text);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditText('');
  };

  const saveEdit = async (todo: Todo) => {
    const text = editText.trim();
    if (!text || text === todo.text) {
      cancelEdit();
      return;
    }
    try {
      setBusyId(todo.id);
      // optimistic
      setTodos((prev) =>
        prev.map((t) => (t.id === todo.id ? { ...t, text } : t))
      );
      await api.patch(`/tickets/${ticketId}/todos/${todo.id}`, { text });
      cancelEdit();
    } catch (e: any) {
      console.error(e);
      setError('Failed to save changes.');
      // revert (re-fetch to be safe)
      fetchTodos();
      cancelEdit();
    } finally {
      setBusyId(null);
    }
  };

  const removeTodo = async (todo: Todo) => {
    try {
      setBusyId(todo.id);
      // optimistic
      const prev = todos;
      setTodos((p) => p.filter((t) => t.id !== todo.id));
      await api.delete(`/tickets/${ticketId}/todos/${todo.id}`);
    } catch (e: any) {
      console.error(e);
      setError('Failed to delete to-do.');
      // re-fetch to restore
      fetchTodos();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className={className}>
      <Card.Header className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <span role="img" aria-label="calendar">🗓️</span>
          <strong>TO-DO</strong>
        </div>
      </Card.Header>

      <Card.Body className="pt-3">
        <InputGroup className="mb-3">
          <Form.Control
            placeholder="Add a to-do"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addTodo();
            }}
          />
          <Button variant="outline-secondary" onClick={addTodo} disabled={busyId === 'new'}>
            {busyId === 'new' ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faPlusCircle} />}
          </Button>
        </InputGroup>

        {error && <div className="text-danger small mb-2">{error}</div>}
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-secondary">
            <Spinner size="sm" animation="border" />
            <span>Loading…</span>
          </div>
        ) : (
          <ListGroup variant="flush" className="todo-list">
            {todos.length === 0 && (
              <div className="text-body-secondary small">No to-dos yet.</div>
            )}

            {todos.map((todo) => {
              const isEditing = editId === todo.id;
              const isBusy = busyId === todo.id;

              return (
                <ListGroup.Item key={todo.id} className="d-flex align-items-start justify-content-between px-0">
                  <div className="d-flex align-items-start gap-2">
                    <Form.Check
                      type="checkbox"
                      className="mt-1"
                      checked={!!todo.done}
                      onChange={() => toggleDone(todo)}
                      disabled={isBusy}
                    />
                    <div className="flex-grow-1">
                      {isEditing ? (
                        <Form.Control
                          size="sm"
                          value={editText}
                          autoFocus
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(todo);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                      ) : (
                        <div
                          className="fw-semibold"
                          style={{
                            textDecoration: todo.done ? 'line-through' : 'none',
                            color: todo.done ? '#6c757d' : 'inherit'
                          }}
                        >
                          {todo.text}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 ms-2">
                    {isEditing ? (
                      <>
                        <Button
                          variant="link"
                          className="p-0"
                          onClick={() => saveEdit(todo)}
                          disabled={isBusy}
                          title="Save"
                        >
                          <FontAwesomeIcon icon={faSave} />
                        </Button>
                        <Button
                          variant="link"
                          className="p-0"
                          onClick={cancelEdit}
                          title="Cancel"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="link"
                          className="p-0"
                          onClick={() => startEdit(todo)}
                          disabled={isBusy}
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Button>
                        <Button
                          variant="link"
                          className="p-0 text-danger"
                          onClick={() => removeTodo(todo)}
                          disabled={isBusy}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </>
                    )}
                  </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}
      </Card.Body>
    </Card>
  );
}
